import { DEFAULT_CAMERA_SETTINGS, type CameraSettings } from '../camera/ThirdPersonCamera';

export type TrailMode = 'flat' | '3d';
export type SlimeStyle = 'transparent' | 'cartoon';

export interface GraphicSettings {
  trailMode: TrailMode;
  slimeStyle: SlimeStyle;
  /** Tampilkan info debug HUD (STATE/FPS/NET). Default off. */
  debugHud: boolean;
}

export const DEFAULT_GRAPHIC_SETTINGS: GraphicSettings = {
  trailMode: 'flat',
  slimeStyle: 'transparent',
  debugHud: false,
};

const STORAGE_KEY = 'slime-brawler:camera-settings';
const GRAPHIC_KEY = 'slime-brawler:graphic-settings';

function sanitize(raw: unknown): CameraSettings {
  const merged: Record<string, unknown> = { ...DEFAULT_CAMERA_SETTINGS };
  if (raw && typeof raw === 'object') {
    const source = raw as Record<string, unknown>;
    for (const key of Object.keys(merged)) {
      const value = source[key];
      if (value !== undefined && typeof value === typeof merged[key]) {
        merged[key] = value;
      }
    }
  }
  return merged as unknown as CameraSettings;
}

export function loadCameraSettings(): CameraSettings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CAMERA_SETTINGS };
    return sanitize(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CAMERA_SETTINGS };
  }
}

export function saveCameraSettings(settings: CameraSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    return;
  }
}

export function clearCameraSettings(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
}

export function loadGraphicSettings(): GraphicSettings {
  try {
    const raw = window.localStorage.getItem(GRAPHIC_KEY);
    if (!raw) return { ...DEFAULT_GRAPHIC_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<GraphicSettings>;
    return {
      trailMode: parsed.trailMode === '3d' ? '3d' : 'flat',
      slimeStyle: parsed.slimeStyle === 'cartoon' ? 'cartoon' : 'transparent',
      debugHud: parsed.debugHud === true,
    };
  } catch {
    return { ...DEFAULT_GRAPHIC_SETTINGS };
  }
}

export function saveGraphicSettings(settings: GraphicSettings): void {
  try {
    window.localStorage.setItem(GRAPHIC_KEY, JSON.stringify(settings));
  } catch {
    return;
  }
}